import { format, isValid, parseISO } from 'date-fns';
import React from 'react';
import { Box, TextField, Tooltip } from '@mui/material';
import { PickersDay, StaticDatePicker } from '@mui/x-date-pickers';
import { BASE_COLORS } from '../../../constants';
import { useDroughtWarning } from './DroughtWarningContext';
import DroughtWarningRainTable from './DroughtWarningRainTable';
import DroughtWarningSummary from './DroughtWarningSummary';
import { getDroughtStatusColors, getDroughtStatusLabel } from './droughtWarningStatusStyles';

const getCalendarDate = (selectedDate) => {
    const parsedDate = parseISO(selectedDate);

    return isValid(parsedDate) ? parsedDate : new Date();
};

const DroughtWarningSidebar = () => {
    const {
        selectedDate,
        setSelectedDate,
        droughtInfoRows,
        droughtInfoLoading,
        selectedMeparCode,
        selectedGridId,
        droughtStatusByDate,
        droughtDayCount,
        evaluatedDayCount,
        droughtStatusesLoading,
    } = useDroughtWarning();
    const calendarDate = getCalendarDate(selectedDate);
    const formattedSelectedDate = format(calendarDate, 'yyyy.MM.dd.');
    const hasSelection = Boolean(selectedMeparCode || selectedGridId);
    const selectedTargetLabel = selectedGridId ? 'grid elemre' : 'MePAR blokkra';

    return (
        <Box
            sx={{
                width: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 1, md: 1.5 },
                    mb: { xs: 1.5, md: 2 },
                }}
            >
                <Box
                    sx={{
                        borderRadius: 1,
                        backgroundColor: BASE_COLORS.PRIMARY_MAIN,
                        width: '100%',
                        '& .MuiPickerStaticWrapper-root': {
                            width: '100% !important',
                            borderRadius: 1,
                        },
                        '& .MuiCalendarOrClockPicker-root': {
                            width: '100% !important',
                            borderRadius: 1,
                        },
                        '& .MuiCalendarPicker-root': {
                            width: '100% !important',
                            borderRadius: 1,
                        },
                        '& .MuiPickersCalendarHeader-root': {
                            px: 1,
                            pt: 1,
                        },
                        '& .MuiPickersCalendarHeader-label': {
                            fontSize: { xs: 13, sm: 14 },
                        },
                        '& .MuiDayPicker-header': {
                            px: 1,
                        },
                        '& .MuiDayPicker-weekContainer': {
                            px: 1,
                        },
                    }}
                >
                    <StaticDatePicker
                        displayStaticWrapperAs="desktop"
                        openTo="day"
                        views={['day']}
                        disableFuture
                        inputFormat="yyyy-MM-dd"
                        value={calendarDate}
                        onChange={(newValue) => {
                            if (newValue && isValid(newValue)) {
                                setSelectedDate(format(newValue, 'yyyy-MM-dd'));
                            }
                        }}
                        renderDay={(day, selectedDays, pickersDayProps) => {
                            const dayKey = format(day, 'yyyy-MM-dd');
                            const hasStatus =
                                !droughtStatusesLoading &&
                                Object.prototype.hasOwnProperty.call(droughtStatusByDate, dayKey);

                            if (!hasStatus || pickersDayProps.outsideCurrentMonth) {
                                return <PickersDay {...pickersDayProps} key={dayKey} />;
                            }

                            const isDrought = droughtStatusByDate[dayKey];
                            const statusColors = getDroughtStatusColors(isDrought);

                            return (
                                <Tooltip
                                    arrow
                                    key={dayKey}
                                    title={getDroughtStatusLabel(isDrought)}
                                >
                                    <PickersDay
                                        {...pickersDayProps}
                                        sx={{
                                            ...statusColors,
                                            border: pickersDayProps.selected
                                                ? `2px solid ${BASE_COLORS.WHITE}`
                                                : '2px solid transparent',
                                            fontWeight: pickersDayProps.selected ? 700 : 500,
                                            '&:hover, &:focus': statusColors,
                                            '&.Mui-selected': {
                                                ...statusColors,
                                                '&:hover, &:focus': statusColors,
                                            },
                                        }}
                                    />
                                </Tooltip>
                            );
                        }}
                        renderInput={(params) => <TextField {...params} />}
                    />
                </Box>
            </Box>

            {hasSelection ? (
                <>
                    <DroughtWarningSummary
                        formattedSelectedDate={formattedSelectedDate}
                        droughtInfoRows={droughtInfoRows}
                        droughtInfoLoading={droughtInfoLoading}
                        selectedTargetLabel={selectedTargetLabel}
                        droughtDayCount={droughtDayCount}
                        evaluatedDayCount={evaluatedDayCount}
                        droughtStatusesLoading={droughtStatusesLoading}
                    />
                    <DroughtWarningRainTable
                        selectedDate={formattedSelectedDate}
                        rows={droughtInfoRows}
                        loading={droughtInfoLoading}
                    />
                </>
            ) : null}
        </Box>
    );
};

export default DroughtWarningSidebar;
